/**
 * HighlightedRitualists Component
 * Section with title, subtitle, right-side links, and horizontal scroll of 300x300 cards.
 */

import { ArrowRight, Loader2 } from "lucide-react"
import { memo } from "react"
import { Link } from "react-router-dom"
import { Carousel } from "@/components/carousel/Carousel"
import { useHighlightRitualistRequest } from "../hooks/useHighlightRitualistRequest"
import type { HighlightedRitualist } from "../types/connect.types"
import { RitualistAvatar } from "./RitualistAvatar"

interface HighlightedRitualistsProps {
  ritualists: HighlightedRitualist[]
  onFollow?: (id: string) => void
  onUnfollow?: (id: string) => void
}

export const HighlightedRitualists = memo(function HighlightedRitualists({
  ritualists,
  onFollow,
  onUnfollow,
}: HighlightedRitualistsProps) {
  const requestHighlight = useHighlightRitualistRequest()

  if (ritualists.length === 0) return null

  return (
    <section className="mb-10 md:py-12 max-w-360 mx-auto px-5 md:px-17.5">
      {/* Header */}
      <div className="flex items-start justify-between mb-2">
        <div>
          <h2 className="text-xl font-semibold md:section-heading text-text-primary mt-1">
            Highlighted Ritualists
          </h2>
          <p className="text-[13px] md:text-[14px] text-primary md:text-text-body mt-1.25 md:mt-2">
            Curated ritual creators aligned with our standards.
          </p>
        </div>
        <div className="hidden md:flex items-center gap-7 text-[14px] leading-[20px] text-text-primary whitespace-nowrap font-semibold mt-2.5">
          <button
            type="button"
            onClick={() => requestHighlight.mutate()}
            disabled={requestHighlight.isPending}
            className="transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-brand-olive hover:text-brand-olive/80 underline underline-offset-2 cursor-pointer"
          >
            {requestHighlight.isPending ? (
              <span className="flex items-center gap-2">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                Requesting...
              </span>
            ) : (
              "Request to be Highlighted"
            )}
          </button>
          <Link
            to="/connect/ritualists"
            className="flex items-center gap-2 hover:text-text-primary transition-colors"
          >
            Explore Ritualists <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>

      {/* Scrollable 300x300 cards */}
      <div className="mt-5 md:mt-8">
        <Carousel
          data={ritualists}
          renderItem={ritualist => (
            <RitualistAvatar ritualist={ritualist} onFollow={onFollow} onUnfollow={onUnfollow} />
          )}
          itemWidth="300px"
          contentClassName="gap-[33px] touch-pan-x"
          opts={{ align: "start", loop: false }}
        />
      </div>
      <div className="md:hidden flex flex-col items-start gap-3.75 text-[14px] leading-[20px] text-text-primary whitespace-nowrap font-semibold mt-5">
        <button
          type="button"
          onClick={() => requestHighlight.mutate()}
          disabled={requestHighlight.isPending}
          className="transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-brand-olive hover:text-brand-olive/80 underline underline-offset-2 cursor-pointer"
        >
          {requestHighlight.isPending ? (
            <span className="flex items-center gap-2">
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              Requesting...
            </span>
          ) : (
            "Request to be Highlighted"
          )}
        </button>
        <Link
          to="/connect/ritualists"
          className="flex items-center gap-2 hover:text-text-primary transition-colors"
        >
          Explore Ritualists <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </section>
  )
})
